import { useState, useCallback } from 'react';

import { Auth } from '../utils/amplifyBootstrap';
import { getApiGatewayUrl } from '../utils/environment';

export interface TOTPSetupResult {
  secretCode: string;
  qrCodeUrl: string;
  message: string;
}

export interface TOTPVerifyResult {
  status: string;
  message: string;
  session?: string;
}

export interface UseTOTPReturn {
  isLoading: boolean;
  error: Error | null;
  setupResult: TOTPSetupResult | null;
  verifyResult: TOTPVerifyResult | null;
  setupTOTP: () => Promise<TOTPSetupResult>;
  verifyTOTP: (totpCode: string) => Promise<TOTPVerifyResult>;
  resetState: () => void;
  completeMfaChallenge: (challengeResponse: string) => Promise<boolean>;
}

export const useTOTP = (): UseTOTPReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [setupResult, setSetupResult] = useState<TOTPSetupResult | null>(null);
  const [verifyResult, setVerifyResult] = useState<TOTPVerifyResult | null>(null);

  const resetState = useCallback(() => {
    setError(null);
    setSetupResult(null);
    setVerifyResult(null);
  }, []);

  const setupTOTP = useCallback(async (): Promise<TOTPSetupResult> => {
    setIsLoading(true);
    setError(null);
    setSetupResult(null);

    try {
      const session = await Auth.currentSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (!accessToken) {
        throw new Error('No access token available');
      }

      const response = await fetch(`${getApiGatewayUrl()}/auth/totp/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const responseJson = await response.json();

      if (responseJson.error) {
        throw new Error(responseJson.error);
      }

      const result: TOTPSetupResult = {
        secretCode: responseJson.secretCode,
        qrCodeUrl: responseJson.qrCodeUrl,
        message: responseJson.message || 'TOTP setup successful',
      };

      setSetupResult(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyTOTP = useCallback(async (totpCode: string): Promise<TOTPVerifyResult> => {
    setIsLoading(true);
    setError(null);
    setVerifyResult(null);

    try {
      const session = await Auth.currentSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (!accessToken) {
        throw new Error('No access token available');
      }

      const response = await fetch(`${getApiGatewayUrl()}/auth/totp/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ totpCode }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseJson = await response.json();

      if (responseJson.error) {
        throw new Error(responseJson.error);
      }

      const result: TOTPVerifyResult = {
        status: responseJson.success,
        message: responseJson.message || 'TOTP verification successful',
      };

      setVerifyResult(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const completeMfaChallenge = useCallback(async (challengeResponse: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await Auth.confirmSignIn({
        challengeResponse,
      });
      return result.isSignedIn;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    error,
    setupResult,
    verifyResult,
    setupTOTP,
    verifyTOTP,
    resetState,
    completeMfaChallenge,
  };
};
